/**
 * Learning API
 *
 * RTK Query endpoints for the learning module catalog with progress tracking.
 */

import { api } from '../api';
import { withRetryAfter } from '../../utils/loadError';

// ============================================
// Types
// ============================================

/** Explicit Learn → Practice link authored on a lesson (absent when unclear). */
export interface LessonPracticeLink {
  /** 'free' = record-and-review speaking practice; 'npc' = NPC role-play. */
  mode: 'free' | 'npc';
  /** mode 'npc' only: NPC persona scenario key to filter by. */
  scenarioKey?: string;
}

export interface LessonDetail {
  id: string;
  title: string;
  duration: string;
  type: 'video' | 'article' | 'exercise';
  practice?: LessonPracticeLink;
  /**
   * false = the lesson has no authored content yet: it cannot be started or
   * completed (the server answers 422 LESSON_CONTENT_UNAVAILABLE) and is not
   * counted in the module's progress.
   */
  contentAvailable?: boolean;
  content?: string;
  contentType?: string;
  contentUrl?: string;
  markdown?: string;
}

export interface LearningModule {
  id: string;
  title: string;
  description: string;
  category: string;
  lessons: number;
  /** Lessons with content — the progress denominator (lessons without content do not count). */
  availableLessons?: number;
  durationMinutes: number;
  slug: string;
  sortOrder: number;
  overview?: string;
  lessonDetails?: LessonDetail[];
  /** Present in listing response when authenticated */
  completedLessons?: number;
  /** Present in detail response when authenticated */
  completedLessonIds?: string[];
  /** Present in detail response when authenticated — lessons with an attempt */
  startedLessonIds?: string[];
  /** Present in detail response — exercise lesson responses keyed by lessonId */
  exerciseResponses?: Record<string, string>;
}

export interface LearningCategory {
  id: string;
  label: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface CompleteLessonResponse {
  moduleSlug: string;
  lessonId: string;
  completedLessonIds: string[];
  completedLessons: number;
  totalLessons: number;
}

interface StartLessonResponse {
  attemptId: string;
  moduleSlug: string;
  lessonId: string;
  status: 'in_progress' | 'completed';
  response?: string | null;
  startedAt: string;
  completedAt: string | null;
}

// ============================================
// API Definition
// ============================================

export const learningApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * List all learning modules (optionally filtered by category)
     */
    getLearningModules: builder.query<
      { modules: LearningModule[]; categories: LearningCategory[] },
      { category?: string } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.category) searchParams.append('category', params.category);
        const qs = searchParams.toString();
        return `/learn/modules${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: ApiResponse<{ modules: LearningModule[]; categories: LearningCategory[] }>) =>
        response.data,
      // The Learn page tells "busy" (429, Retry-After) from "failed" from an empty catalog.
      transformErrorResponse: (error, meta) => withRetryAfter(error, meta),
      providesTags: ['LearningModules'],
    }),

    /**
     * Get a single learning module by slug
     */
    getLearningModule: builder.query<LearningModule, string>({
      query: (slug) => `/learn/modules/${slug}`,
      transformResponse: (response: ApiResponse<LearningModule>) => response.data,
      providesTags: (_result, _error, slug) => [{ type: 'LearningModules', id: slug }],
    }),

    /**
     * Start/resume a lesson attempt (idempotent)
     */
    startLesson: builder.mutation<StartLessonResponse, { slug: string; lessonId: string; response?: string }>({
      query: ({ slug, lessonId, response }) => ({
        url: `/learn/modules/${slug}/lessons/${lessonId}/start`,
        method: 'POST',
        body: response !== undefined ? { response } : undefined,
      }),
      transformResponse: (response: ApiResponse<StartLessonResponse>) => response.data,
      invalidatesTags: (_result, _error, { slug }) => [
        { type: 'LearningModules', id: slug },
      ],
    }),

    /**
     * Mark a lesson as completed (idempotent)
     */
    completeLesson: builder.mutation<CompleteLessonResponse, { slug: string; lessonId: string }>({
      query: ({ slug, lessonId }) => ({
        url: `/learn/modules/${slug}/lessons/${lessonId}/complete`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CompleteLessonResponse>) => response.data,
      invalidatesTags: (_result, _error, { slug }) => [
        'LearningModules',
        { type: 'LearningModules', id: slug },
      ],
    }),
  }),
  overrideExisting: false,
});

// Export hooks
export const {
  useGetLearningModulesQuery,
  useGetLearningModuleQuery,
  useStartLessonMutation,
  useCompleteLessonMutation,
} = learningApi;
